import React, { useCallback, useRef, useState } from "react";
import styled from "styled-components";

import { ImageBoardItem } from "../../app/services/imageBoard";

/** 位置・サイズの部分更新（ワールド座標） */
export interface GeometryPatch {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

interface Geometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

type ResizeCorner = "nw" | "ne" | "sw" | "se";
type GestureMode = "move" | ResizeCorner;

interface GestureState {
  pointerId: number;
  mode: GestureMode;
  startClientX: number;
  startClientY: number;
  startGeom: Geometry;
  moved: boolean;
}

interface BoardImageItemProps {
  item: ImageBoardItem;
  /** 現在のズーム倍率（スクリーン距離 → ワールド距離の変換に使う） */
  scale: number;
  selected: boolean;
  /**
   * リサイズハンドルを出すか。複数選択中は 1 枚だけを変形させる操作に
   * 意味がないため、単独選択のときだけ true になる。
   */
  showHandles: boolean;
  /** 閲覧のみ（公開閲覧）。選択はできるが移動・リサイズはしない */
  readOnly?: boolean;
  /** `additive` は Shift クリック（選択への追加 / 解除）を表す */
  // eslint-disable-next-line no-unused-vars
  onSelect: (id: string, additive: boolean) => void;
  /** ドラッグ/リサイズ確定時に呼ばれる（PATCH 保存用） */
  // eslint-disable-next-line no-unused-vars
  onCommitGeometry: (id: string, patch: GeometryPatch) => void;
}

/** ワールド単位での最小サイズ */
const MIN_SIZE = 24;
/** これ未満の移動はクリック扱いにする（スクリーン px） */
const MOVE_THRESHOLD_PX = 3;

const ItemWrapper = styled.div<{ $selected: boolean; $readOnly: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  cursor: ${({ $selected, $readOnly }) =>
    $selected && !$readOnly ? "move" : "pointer"};
  outline: none;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: fill;
    pointer-events: none;
    user-select: none;
    -webkit-user-drag: none;
  }
`;

const SelectionRing = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-style: solid;
  border-color: var(--soremo-blue);
`;

const ResizeHandle = styled.div<{ $corner: ResizeCorner }>`
  position: absolute;
  background: #ffffff;
  border-style: solid;
  border-color: var(--soremo-blue);
  cursor: ${({ $corner }) =>
    $corner === "nw" || $corner === "se" ? "nwse-resize" : "nesw-resize"};
`;

/**
 * イメージボード上の画像 1 枚。
 *
 * - ドラッグで移動、選択時の四隅ハンドルでリサイズ（アスペクト比維持）
 * - Shift + クリックで選択に追加 / 解除する（この場合は移動を開始しない）
 * - ジェスチャー中はローカル state（draft）で描画し、確定時のみ親へ commit する
 * - 枠線・ハンドルはズーム倍率の逆数でスケールし、見かけの太さを一定に保つ
 */
const BoardImageItem: React.FC<BoardImageItemProps> = ({
  item,
  scale,
  selected,
  showHandles,
  readOnly = false,
  onSelect,
  onCommitGeometry,
}) => {
  const [draft, setDraft] = useState<Geometry | null>(null);
  const gestureRef = useRef<GestureState | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const geom: Geometry = draft ?? {
    x: item.x,
    y: item.y,
    width: item.width,
    height: item.height,
  };

  const beginGesture = useCallback(
    (e: React.PointerEvent, mode: GestureMode) => {
      // 右クリック等は無視（タッチ/ペンは button === 0 相当）
      if (e.button !== 0) return;
      e.stopPropagation();
      e.preventDefault();
      onSelect(item.id, e.shiftKey);

      // Shift クリックは「選択の付け外し」専用。そのままドラッグしても
      // 移動しないことで、複数選択の組み立て中に配置が崩れるのを防ぐ。
      if (e.shiftKey || readOnly) return;

      gestureRef.current = {
        pointerId: e.pointerId,
        mode,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startGeom: {
          x: item.x,
          y: item.y,
          width: item.width,
          height: item.height,
        },
        moved: false,
      };
      // ラッパーがキャプチャすることで、以降の pointermove / pointerup は
      // （リサイズハンドル起点のジェスチャーでも）ItemWrapper 側に届く。
      wrapperRef.current?.setPointerCapture(e.pointerId);
    },
    [item.id, item.x, item.y, item.width, item.height, onSelect, readOnly],
  );

  const computeGeometry = useCallback(
    (gesture: GestureState, clientX: number, clientY: number): Geometry => {
      const dx = (clientX - gesture.startClientX) / scale;
      const dy = (clientY - gesture.startClientY) / scale;
      const start = gesture.startGeom;

      if (gesture.mode === "move") {
        return { ...start, x: start.x + dx, y: start.y + dy };
      }

      // リサイズ: 横方向のドラッグ量を基準にアスペクト比を維持する
      const aspect = start.width / start.height;
      const isLeft = gesture.mode === "nw" || gesture.mode === "sw";
      const isTop = gesture.mode === "nw" || gesture.mode === "ne";

      const rawWidth = isLeft ? start.width - dx : start.width + dx;
      const width = Math.max(MIN_SIZE, rawWidth);
      const height = Math.max(MIN_SIZE / aspect, width / aspect);

      return {
        width,
        height,
        x: isLeft ? start.x + (start.width - width) : start.x,
        y: isTop ? start.y + (start.height - height) : start.y,
      };
    },
    [scale],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const gesture = gestureRef.current;
      if (!gesture || e.pointerId !== gesture.pointerId) return;
      e.stopPropagation();

      if (!gesture.moved) {
        const dist = Math.hypot(
          e.clientX - gesture.startClientX,
          e.clientY - gesture.startClientY,
        );
        if (dist < MOVE_THRESHOLD_PX) return;
        gesture.moved = true;
      }
      setDraft(computeGeometry(gesture, e.clientX, e.clientY));
    },
    [computeGeometry],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      const gesture = gestureRef.current;
      if (!gesture || e.pointerId !== gesture.pointerId) return;
      e.stopPropagation();
      gestureRef.current = null;

      if (gesture.moved) {
        const finalGeom = computeGeometry(gesture, e.clientX, e.clientY);
        const patch: GeometryPatch = {};
        if (finalGeom.x !== gesture.startGeom.x) patch.x = finalGeom.x;
        if (finalGeom.y !== gesture.startGeom.y) patch.y = finalGeom.y;
        if (finalGeom.width !== gesture.startGeom.width)
          patch.width = finalGeom.width;
        if (finalGeom.height !== gesture.startGeom.height)
          patch.height = finalGeom.height;
        if (Object.keys(patch).length > 0) {
          // 楽観更新によりキャッシュへ同期反映されるため draft は破棄してよい
          onCommitGeometry(item.id, patch);
        }
      }
      setDraft(null);
    },
    [computeGeometry, item.id, onCommitGeometry],
  );

  // ズームしても見かけの太さ/大きさが一定になるように逆数スケール
  const ringWidth = 1.5 / scale;
  const handleSize = 10 / scale;
  const handleBorder = 1.5 / scale;
  const handleOffset = -handleSize / 2;

  const cornerPosition: Record<ResizeCorner, React.CSSProperties> = {
    nw: { top: handleOffset, left: handleOffset },
    ne: { top: handleOffset, right: handleOffset },
    sw: { bottom: handleOffset, left: handleOffset },
    se: { bottom: handleOffset, right: handleOffset },
  };

  return (
    <ItemWrapper
      ref={wrapperRef}
      $selected={selected}
      $readOnly={readOnly}
      style={{
        transform: `translate(${geom.x}px, ${geom.y}px)`,
        width: geom.width,
        height: geom.height,
        zIndex: item.z_index,
      }}
      onPointerDown={(e) => beginGesture(e, "move")}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <img src={item.image_url} alt="" loading="lazy" draggable={false} />

      {selected && <SelectionRing style={{ borderWidth: ringWidth }} />}

      {selected &&
        showHandles &&
        !readOnly &&
        (Object.keys(cornerPosition) as ResizeCorner[]).map((corner) => (
          <ResizeHandle
            key={corner}
            $corner={corner}
            style={{
              ...cornerPosition[corner],
              width: handleSize,
              height: handleSize,
              borderWidth: handleBorder,
            }}
            onPointerDown={(e) => beginGesture(e, corner)}
          />
        ))}
    </ItemWrapper>
  );
};

export default BoardImageItem;
